import { create } from 'zustand';
import type { Lang } from '@/i18n/dict';

export type SteleView =
  | 'idle-orb'
  | 'idle-content'
  | 'calling'
  | 'incall'
  | 'waiting'
  | 'unavailable'
  | 'feedback'
  | 'goodbye';

export interface SteleState {
  view: SteleView;
  lang: Lang;
  volume: number;
  translationOn: boolean;
  langOverlayOpen: boolean;
  consentOpen: boolean;
  consentTopic?: string;
  consentResponder?: ((accepted: boolean) => void) | null;
  consentNotice: 'cancelled' | null;
  rating: number | null;
  steleId: string | null;
  callId: string | null;
  /** Welle 6: Letzte callId überlebt den Wechsel ins Feedback-View damit das Feedback POSTbar bleibt. */
  lastCallId: string | null;
  rtcToken: string | null;
  /** Welle 9: Echte Stelen-Identität (vom Backend), ersetzt i18n-Platzhalter. */
  identity: { steleName: string; tenantName: string; location: string | null; logo?: string | null } | null;
  /** Welle 9: Name des verbundenen Beraters (aus rtc:peer-joined). */
  connectedAgentName: string | null;
  /** pre184: Grund für die Unavailable-Ansicht ('no-agent' = kein Berater verfügbar). */
  unavailReason: 'no-agent' | 'hours' | null;
  /** pre184: Kundenadmin-Hinweistext bei 'no-agent' (vom Backend, sonst i18n-Default). */
  noAgentNotice: string | null;
  /** pre185: Live-Verfügbarkeit der Stele-Gruppe (null = unbekannt/Endpoint fehlt → wie bisher). */
  agentsAvailable: boolean | null;
  availabilityNotice: string | null;
  refreshAvailability: () => Promise<void>;
  setAvailability: (available: boolean | null, notice: string | null) => void;

  setView: (v: SteleView) => void;
  setLang: (l: Lang) => void;
  setVolume: (delta: number) => void;
  toggleTranslation: () => void;
  openLangOverlay: () => void;
  closeLangOverlay: () => void;
  openConsent: (topic?: string, responder?: (accepted: boolean) => void) => void;
  closeConsent: () => void;
  respondConsent: (accepted: boolean) => void;
  setConsentNotice: (n: 'cancelled' | null) => void;
  setRating: (r: number) => void;
  setSteleId: (id: string) => void;
  setIdentity: (i: SteleState['identity']) => void;
  setConnectedAgentName: (n: string | null) => void;
  startCall: () => Promise<void>;
  endCall: () => Promise<void>;
}

export const useSteleStore = create<SteleState>((set, get) => ({
  view: 'idle-orb',
  lang: 'de',
  volume: 60,
  translationOn: false,
  langOverlayOpen: false,
  consentOpen: false,
  consentTopic: undefined,
  consentResponder: null,
  consentNotice: null,
  rating: null,
  steleId: null,
  callId: null,
  lastCallId: null,
  rtcToken: null,
  identity: null,
  connectedAgentName: null,
  unavailReason: null,
  noAgentNotice: null,
  agentsAvailable: null,
  availabilityNotice: null,

  setView: (view) => set({ view }),
  setLang: (lang) => {
    document.documentElement.setAttribute('dir', lang === 'ar' ? 'rtl' : 'ltr');
    document.documentElement.setAttribute('lang', lang);
    set({ lang });
  },
  setVolume: (delta) => set((s) => ({ volume: Math.max(0, Math.min(100, s.volume + delta * 10)) })),
  toggleTranslation: () => set((s) => ({ translationOn: !s.translationOn })),
  openLangOverlay: () => set({ langOverlayOpen: true }),
  closeLangOverlay: () => set({ langOverlayOpen: false }),
  openConsent: (topic, responder) => set({ consentOpen: true, consentTopic: topic, consentResponder: responder ?? null }),
  closeConsent: () => set({ consentOpen: false, consentResponder: null }),
  respondConsent: (accepted) => {
    const r = get().consentResponder;
    try { r?.(accepted); } catch { /* */ }
    set({ consentOpen: false, consentResponder: null });
  },
  setConsentNotice: (n) => set({ consentNotice: n }),
  setRating: (rating) => set({ rating }),
  setSteleId: (steleId) => set({ steleId }),
  setIdentity: (identity) => set({ identity }),
  setConnectedAgentName: (connectedAgentName) => set({ connectedAgentName }),

  setAvailability: (available, notice) => set({ agentsAvailable: available, availabilityNotice: notice }),

  refreshAvailability: async () => {
    const { steleId } = get();
    if (!steleId) return;
    try {
      const res = await fetch(`/api/calls/availability/${steleId}`);
      if (!res.ok) { set({ agentsAvailable: null }); return; } // Endpoint fehlt/Fehler → unbekannt (zeigt Start wie bisher)
      const j = await res.json();
      set({
        agentsAvailable: typeof j?.available === 'boolean' ? j.available : null,
        availabilityNotice: (typeof j?.notice === 'string' && j.notice.trim()) ? j.notice : null,
      });
    } catch { set({ agentsAvailable: null }); }
  },

  startCall: async () => {
    const { steleId } = get();
    set({ view: 'calling', unavailReason: null, noAgentNotice: null });
    if (!steleId) {
      console.warn('[stele] keine steleId konfiguriert - Demo-Modus');
      setTimeout(() => set({ view: 'incall' }), 2200);
      return;
    }
    try {
      const res = await fetch('/api/calls/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ steleId }),
      });
      // pre184: Kein verfügbarer Berater → Backend antwortet 409 { reason:'no-agent', notice }.
      // Kein Call wird aufgebaut; stattdessen Hinweis (kundenadmin-änderbar) zeigen.
      if (res.status === 409) {
        let notice: string | null = null;
        try { const j = await res.json(); if (j?.reason === 'no-agent') notice = (typeof j?.notice === 'string' && j.notice.trim()) ? j.notice : null; } catch { /* */ }
        console.log('[stele] initiate abgelehnt: kein Berater verfügbar');
        set({ view: 'unavailable', unavailReason: 'no-agent', noAgentNotice: notice });
        return;
      }
      if (!res.ok) {
        const t = await res.text();
        throw new Error(`initiate ${res.status}: ${t}`);
      }
      const { callId, rtcToken } = await res.json();
      console.log(`[stele] call initiated: ${callId}`);
      set({ callId, rtcToken });
      // Direkter Übergang in incall — WebRTC-Hook startet dann getUserMedia
      setTimeout(() => set({ view: 'incall' }), 800);
    } catch (e) {
      console.error('[stele] initiate fehlgeschlagen', e);
      // pre184: kein blinder Fallback mehr in 'incall' — bei Fehler ebenfalls Hinweis zeigen.
      set({ view: 'unavailable', unavailReason: 'no-agent', noAgentNotice: null });
    }
  },

  endCall: async () => {
    const { callId } = get();
    if (callId) {
      try {
        await fetch(`/api/calls/${callId}/end`, { method: 'POST' });
      } catch (e) {
        console.warn('[stele] end-Call fehlgeschlagen', e);
      }
    }
    set({ callId: null, lastCallId: callId, rtcToken: null, view: 'feedback', connectedAgentName: null });
  },
}));
